import { restoreDiacriticsForAsciiFoldedFinnishCityName } from './finland-hub-config';

/** First token of a "remote scope" slug (e.g. "remote_europe"). No real city carries these names. */
const REMOTE_SCOPE_MARKER_WORDS = new Set(['remote', 'etätyö', 'etatyo', 'distans']);

// Country-name suffixes checked case-insensitively (see capitalizeLocationWords below for why:
// `toLocationSlug` lowercases every real-world slug, so "states"/"kingdom"/… is what actually
// arrives here, not "States"/"Kingdom").
const COUNTRY_SUFFIXES_LOWER = ['states', 'kingdom', 'arabia', 'emirates', 'republic', 'federation'];

// Diacritic words used in real "location" facet values (e.g. "Etätyö - Suomi", the literal
// remote-scope facet tagged on Finnish remote jobs) that aren't place names, so the GeoNames
// gazetteer below doesn't know them. toLocationSlug ASCII-folds "Etätyö" to "etatyo" the same as
// any other diacritic word, so decode needs its own tiny restoration table for these.
const ASCII_FOLDED_NON_PLACE_WORDS: Record<string, string> = { etatyo: 'etätyö' };

/**
 * Restores the diacritics a TEC-472 ASCII-folded slug word lost ("jyvaskyla" -> "jyväskylä") for
 * one of this app's curated Finnish hub cities, ignoring (and preserving) any trailing
 * punctuation attached to the word by the caller's split (e.g. "jyvaskyla," from
 * "jyvaskyla, finland"). Falls back to the original word untouched when it isn't a known curated
 * city with a diacritic form - already-accented input, non-Finnish words (Helsinki, Finland,
 * United, States, ...), and separators all pass through unchanged.
 */
function restoreDiacriticsPreservingPunctuation(word: string): string {
  const match = word.match(/^([^,]*)(,*)$/);
  const core = match ? match[1] : word;
  const trailingPunctuation = match ? match[2] : '';
  if (!core) return word;
  const restored =
    ASCII_FOLDED_NON_PLACE_WORDS[core.toLowerCase()] ?? restoreDiacriticsForAsciiFoldedFinnishCityName(core);
  return restored ? `${restored}${trailingPunctuation}` : word;
}

/**
 * Title-cases a decoded location string word-by-word (first letter upper, rest lower).
 * `toLocationSlug` lowercases every slug for canonical URLs, so decoding has to restore display
 * casing rather than assume it's already there. Splitting on plain spaces means punctuation
 * stays attached to its word ("helsinki," → "Helsinki,"), which is what callers here need.
 *
 * Also restores diacritics for known curated Finnish hub cities before capitalizing: `toLocationSlug`
 * ASCII-folds "Jyväskylä" to "jyvaskyla" (TEC-472), so a slug decoded straight back to display
 * text would otherwise show "Jyvaskyla" - and, more importantly, get used as a search-facet
 * filter value that no longer matches the real (accented) facet in the index, silently zeroing
 * out results for every Finnish location page whose name has a diacritic (see
 * utils/search-router/url-handling.ts's refinementList.location, which is built from this
 * function's output).
 */
function capitalizeLocationWords(s: string): string {
  return s
    .split(' ')
    .map((word) => {
      if (!word.length) return word;
      const restored = restoreDiacriticsPreservingPunctuation(word);
      return restored.charAt(0).toUpperCase() + restored.slice(1).toLowerCase();
    })
    .join(' ');
}

/**
 * Reverses {@link toLocationSlug} for URL path segments: turns slugs like
 * `helsinki_finland` or `remote_europe` back into facet/display strings (`Helsinki, Finland`,
 * `Remote - Europe`). `toLocationSlug` always lowercases its output for canonical URLs, so this
 * has to re-capitalize on the way back out rather than merely swap underscores for
 * spaces/commas. Passing the lowercase slug straight through was the source of city/country
 * names rendering as "helsinki" instead of "Helsinki" wherever a saved search or SEO title was
 * built directly from the stored URL instead of a live facet value.
 */
export function decodeLocationSlug(raw: string | null | undefined): string {
  if (!raw) return '';
  let decoded = raw;
  try {
    decoded = decodeURIComponent(raw);
  } catch {
    // ignore decoding issues
  }
  // Defensive fallback for a stale bookmark/cache still using the old "_-_" slug shape.
  if (decoded.includes('_-_')) {
    return capitalizeLocationWords(decoded.replace(/_/g, ' ').trim());
  }
  if (decoded.includes('_')) {
    const parts = decoded.split('_');
    // Remote-scope hubs ("remote_europe") reconstruct with " - ", not ", " (matching the literal
    // facet value used to tag remote-scoped jobs, e.g. "Remote - Europe"). Safe to special-case
    // unconditionally: no real city is named Remote/Etätyö/Distans.
    if (parts.length === 2 && REMOTE_SCOPE_MARKER_WORDS.has(parts[0].toLowerCase())) {
      return `${capitalizeLocationWords(parts[0])} - ${capitalizeLocationWords(parts[1])}`.trim();
    }
    if (parts.length === 2) {
      const [, second] = parts;
      if (COUNTRY_SUFFIXES_LOWER.includes(second.toLowerCase())) {
        return capitalizeLocationWords(decoded.replace(/_/g, ' ')).trim();
      }
    } else if (parts.length >= 3) {
      const lastPart = parts[parts.length - 1];
      if (COUNTRY_SUFFIXES_LOWER.includes(lastPart.toLowerCase())) {
        if (lastPart.toLowerCase() === 'states' && parts[parts.length - 2].toLowerCase() === 'united') {
          const cityParts = parts.slice(0, -2);
          return `${capitalizeLocationWords(cityParts.join(' '))}, United States`.trim();
        }
        const cityParts = parts.slice(0, -1);
        return `${capitalizeLocationWords(cityParts.join(' '))}, ${capitalizeLocationWords(lastPart)}`.trim();
      }
    }
    return capitalizeLocationWords(decoded.replace(/_/g, ', ')).trim();
  }
  return capitalizeLocationWords(decoded.trim());
}
